/**
 * Support Anti-Reform chooser — V2 dialog + optional Stage 2 checkout wiring.
 * Checkout runs only when variant map data attributes are present.
 * Does not complete payment; redirects to Shopify checkout when configured.
 *
 * Modal architecture: on open, the <dialog> is moved to document.body so it
 * escapes section/theme stacking contexts (sticky header/footer/chrome),
 * then opened with showModal() for the browser top layer.
 */
(function () {
  var OPEN_CLASS = "ar-support-dialog-open";
  var scrollLockY = 0;

  function labelFor(amount, frequency) {
    var freq =
      frequency === "monthly" ? "Monthly Support" : "One-off Support";
    return "Selected: £" + amount + " " + freq;
  }

  function parseVariantMap(root) {
    var raw = root.getAttribute("data-variant-map");
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function setFeedback(el, message, isError) {
    if (!el) return;
    el.hidden = !message;
    el.textContent = message || "";
    el.classList.toggle("ar-support-chooser__reassure--error", !!isError);
  }

  function cartAdd(items) {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ items: items }),
      credentials: "same-origin",
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          var msg =
            (data && (data.description || data.message)) ||
            "Could not add support to cart.";
          throw new Error(msg);
        }
        return data;
      });
    });
  }

  function cartClear() {
    return fetch("/cart/clear.js", {
      method: "POST",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    }).then(function (res) {
      if (!res.ok) throw new Error("Could not clear cart.");
      return res.json();
    });
  }

  function getCart() {
    return fetch("/cart.js", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    }).then(function (res) {
      if (!res.ok) throw new Error("Could not read cart.");
      return res.json();
    });
  }

  function isSupportLine(line, supportVariantIds) {
    if (!line || !line.variant_id) return false;
    return supportVariantIds.indexOf(String(line.variant_id)) !== -1;
  }

  function bindChooser(root) {
    if (!root || root.dataset.arSupportBound === "1") return;
    root.dataset.arSupportBound = "1";

    var summary = root.querySelector("[data-ar-support-summary]");
    var feedback = root.querySelector("[data-ar-support-feedback]");
    var cta = root.querySelector("[data-ar-support-cta]");
    var recurring = root.querySelector("[data-ar-support-recurring]");
    var termsMonthly = root.querySelector("[data-ar-support-terms-monthly]");
    var termsOneOff = root.querySelector("[data-ar-support-terms-oneoff]");
    var variantMap = parseVariantMap(root);
    var monthlyPlanId = root.getAttribute("data-monthly-selling-plan") || "";
    var monthlyEnabled = root.getAttribute("data-monthly-enabled") === "1";

    function currentAmount() {
      var el = root.querySelector("[data-ar-support-amount]:checked");
      return el ? el.value : "5";
    }
    function currentFrequency() {
      var el = root.querySelector("[data-ar-support-frequency]:checked");
      return el ? el.value : "monthly";
    }
    function refresh() {
      var frequency = currentFrequency();
      var isMonthly = frequency === "monthly";
      if (summary) summary.textContent = labelFor(currentAmount(), frequency);
      if (recurring) recurring.hidden = !isMonthly;
      if (termsMonthly) termsMonthly.hidden = !isMonthly;
      if (termsOneOff) termsOneOff.hidden = isMonthly;
      setFeedback(feedback, "", false);
    }

    root.addEventListener("change", function (e) {
      if (
        e.target &&
        (e.target.hasAttribute("data-ar-support-amount") ||
          e.target.hasAttribute("data-ar-support-frequency"))
      ) {
        refresh();
      }
    });

    if (cta) {
      cta.addEventListener("click", function (e) {
        e.preventDefault();
        refresh();

        var amount = currentAmount();
        var frequency = currentFrequency();

        if (frequency === "monthly" && !monthlyEnabled) {
          setFeedback(
            feedback,
            "Monthly support is not available yet. Please choose one-off support, or try again later.",
            true
          );
          return;
        }

        if (!variantMap || !variantMap[amount]) {
          setFeedback(
            feedback,
            "Support checkout is not available yet. Please try again later.",
            true
          );
          return;
        }

        var variantId = String(variantMap[amount]);
        var supportIds = Object.keys(variantMap).map(function (k) {
          return String(variantMap[k]);
        });
        var item = { id: Number(variantId), quantity: 1 };
        if (frequency === "monthly" && monthlyPlanId) {
          item.selling_plan = Number(monthlyPlanId);
        }

        cta.disabled = true;
        setFeedback(feedback, "Taking you to secure checkout…", false);

        getCart()
          .then(function (cart) {
            var hasMerch = (cart.items || []).some(function (line) {
              return !isSupportLine(line, supportIds);
            });
            var hasSupport = (cart.items || []).some(function (line) {
              return isSupportLine(line, supportIds);
            });

            if (hasMerch) {
              throw new Error(
                "Your cart already has merchandise. Please complete or clear that cart before supporting, so your items are not removed."
              );
            }
            if (hasSupport || (cart.item_count || 0) > 0) {
              return cartClear().then(function () {
                return cartAdd([item]);
              });
            }
            return cartAdd([item]);
          })
          .then(function () {
            window.location.href = "/checkout";
          })
          .catch(function (err) {
            cta.disabled = false;
            setFeedback(
              feedback,
              (err && err.message) || "Could not start checkout. Please try again.",
              true
            );
          });
      });
    }

    refresh();
  }

  var refreshSupportCtaState = null;

  function lockPageScroll() {
    scrollLockY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add(OPEN_CLASS);
    document.body.classList.add(OPEN_CLASS);
    document.body.style.position = "fixed";
    document.body.style.top = "-" + scrollLockY + "px";
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    if (typeof refreshSupportCtaState === "function") refreshSupportCtaState();
  }

  function unlockPageScroll() {
    document.documentElement.classList.remove(OPEN_CLASS);
    document.body.classList.remove(OPEN_CLASS);
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";
    document.body.style.overflow = "";
    window.scrollTo(0, scrollLockY || 0);
    if (typeof refreshSupportCtaState === "function") refreshSupportCtaState();
  }

  function bindDialog() {
    var dialog =
      document.getElementById("ar-support-dialog") ||
      document.getElementById("ar-support-prototype-dialog");
    if (!dialog || dialog.dataset.arSupportDialogBound === "1") return;
    dialog.dataset.arSupportDialogBound = "1";

    var homeParent = dialog.parentNode;
    var homeNext = dialog.nextSibling;
    var isOpen = false;
    var lastOpener = null;

    function mountToBody() {
      if (dialog.parentNode !== document.body) {
        document.body.appendChild(dialog);
      }
    }

    function restoreHome() {
      if (!homeParent || !homeParent.isConnected) return;
      if (dialog.parentNode === homeParent) return;
      if (homeNext && homeNext.parentNode === homeParent) {
        homeParent.insertBefore(dialog, homeNext);
      } else {
        homeParent.appendChild(dialog);
      }
    }

    function openDialog(opener) {
      if (opener && opener.focus) lastOpener = opener;
      mountToBody();
      if (typeof dialog.showModal === "function") {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute("open", "");
      }
      if (!isOpen) {
        isOpen = true;
        lockPageScroll();
      }
      var closeBtn = dialog.querySelector("[data-ar-support-close]");
      var first = dialog.querySelector("[data-ar-support-amount]:checked");
      if (closeBtn) closeBtn.focus();
      else if (first) first.focus();
    }

    function closeDialog() {
      if (typeof dialog.close === "function") {
        if (dialog.open) dialog.close();
      } else {
        dialog.removeAttribute("open");
      }
      if (isOpen) {
        isOpen = false;
        unlockPageScroll();
      }
      restoreHome();
      if (lastOpener && typeof lastOpener.focus === "function") {
        try {
          lastOpener.focus({ preventScroll: true });
        } catch (e) {
          lastOpener.focus();
        }
      }
    }

    document.querySelectorAll("[data-ar-support-open]").forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        openDialog(btn);
      });
    });

    dialog.querySelectorAll("[data-ar-support-close]").forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        closeDialog();
      });
    });

    // Escape → cancel event (native). Keep close path consistent.
    // Backdrop click: unchanged from prior design (native dialog default / no custom dismiss).
    dialog.addEventListener("cancel", function (e) {
      e.preventDefault();
      closeDialog();
    });

    dialog.addEventListener("close", function () {
      if (isOpen) {
        isOpen = false;
        unlockPageScroll();
      }
      restoreHome();
      if (lastOpener && typeof lastOpener.focus === "function") {
        try {
          lastOpener.focus({ preventScroll: true });
        } catch (e2) {
          lastOpener.focus();
        }
      }
    });

    function hashShouldOpen() {
      // Discovery hashes (#ar-support) scroll only — do not open the transaction modal.
      // Explicit dialog deep-links may still open when a modal trigger exists (Take Action).
      var h = window.location.hash;
      return h === "#ar-support-prototype" || h === "#ar-support-dialog";
    }
    function openFromHash() {
      var h = window.location.hash;
      if (h === "#ar-support") {
        var fund = document.getElementById("ar-support");
        if (fund && typeof fund.scrollIntoView === "function") {
          try {
            fund.scrollIntoView({ block: "start", behavior: "smooth" });
          } catch (e) {
            fund.scrollIntoView(true);
          }
        }
        return;
      }
      if (!hashShouldOpen()) return;
      var opener = document.querySelector("[data-ar-support-open]");
      if (opener) openDialog(opener);
    }
    if (hashShouldOpen()) {
      // Allow layout/paint before opening (footer deep-link / preview)
      window.setTimeout(openFromHash, 50);
    }
    window.addEventListener("hashchange", openFromHash);
  }

  /**
   * One Support CTA, two presentations:
   * - Homepage origin (in-flow) while its original hero slot is visible
   * - Persistent full-width bar after the origin scrolls away (and on other pages)
   * Suppress bar while Support section meaningfully fills the viewport or dialog is open.
   */
  function bindSupportCtaState() {
    var origin = document.querySelector("[data-ar-support-cta-origin]");
    var bar = document.querySelector("[data-ar-support-cta-bar]");
    if (!bar) return;

    var sectionTargets = [];
    document.querySelectorAll("[data-ar-support-section]").forEach(function (el) {
      if (el.closest("dialog")) return;
      sectionTargets.push(el);
    });
    var homeSupport = document.getElementById("ar-support");
    if (
      homeSupport &&
      !homeSupport.closest("dialog") &&
      sectionTargets.indexOf(homeSupport) === -1
    ) {
      sectionTargets.push(homeSupport);
    }

    function sectionMeaningfullyVisible(el) {
      var r = el.getBoundingClientRect();
      var vv = window.visualViewport;
      var viewTop = vv ? vv.offsetTop : 0;
      var viewH = vv ? vv.height : window.innerHeight;
      var viewBottom = viewTop + viewH;
      var overlap = Math.min(r.bottom, viewBottom) - Math.max(r.top, viewTop);
      if (overlap <= 0) return false;
      return overlap >= viewH * 0.28;
    }

    function originVisibleInViewport() {
      if (!origin) return false;
      var r = origin.getBoundingClientRect();
      var vv = window.visualViewport;
      var viewTop = vv ? vv.offsetTop : 0;
      var viewH = vv ? vv.height : window.innerHeight;
      var viewBottom = viewTop + viewH;
      var overlap = Math.min(r.bottom, viewBottom) - Math.max(r.top, viewTop);
      // Any meaningful slice of the in-flow CTA keeps the persistent bar off
      // so the two never compete visually.
      return overlap > 12;
    }

    function setBarActive(active) {
      var isActive = document.documentElement.classList.contains(
        "ar-support-bar-active"
      );
      if (active === isActive && bar.hidden === !active) return;
      if (active) {
        bar.hidden = false;
        bar.removeAttribute("aria-hidden");
        if ("inert" in bar) bar.inert = false;
        document.documentElement.classList.add("ar-support-bar-active");
      } else {
        bar.hidden = true;
        bar.setAttribute("aria-hidden", "true");
        if ("inert" in bar) bar.inert = true;
        document.documentElement.classList.remove("ar-support-bar-active");
      }
    }

    function refresh() {
      var dialogOpen = document.documentElement.classList.contains(
        "ar-support-dialog-open"
      );
      var sectionHit =
        sectionTargets.length > 0 && sectionTargets.some(sectionMeaningfullyVisible);
      var inFlow = originVisibleInViewport();
      // Homepage: bar only after origin scrolls away.
      // Other pages (no origin): bar available unless section/dialog suppress.
      var showBar = !dialogOpen && !sectionHit && !inFlow;
      setBarActive(showBar);
    }

    refreshSupportCtaState = refresh;

    if (typeof IntersectionObserver === "function") {
      var observer = new IntersectionObserver(
        function () {
          refresh();
        },
        { threshold: [0, 0.05, 0.1, 0.2, 0.35, 0.5, 0.75, 1] }
      );
      if (origin) observer.observe(origin);
      sectionTargets.forEach(function (el) {
        observer.observe(el);
      });
    }
    window.addEventListener("scroll", refresh, { passive: true });
    window.addEventListener("resize", refresh);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", refresh);
      window.visualViewport.addEventListener("scroll", refresh);
    }
    // Dialog open/close refreshes via lockPageScroll/unlockPageScroll.
    refresh();
  }

  function init() {
    document.querySelectorAll("[data-ar-support-chooser]").forEach(bindChooser);
    bindDialog();
    bindSupportCtaState();
  }

  function safeInit() {
    try {
      init();
    } catch (err) {
      // Support enhancement must never blank the homepage. Origin CTA remains
      // a normal link to Take Action #support even if persistence JS fails.
      if (typeof console !== "undefined" && console.warn) {
        console.warn("[ar-support] init failed; in-flow Support link still available", err);
      }
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", safeInit);
  } else {
    safeInit();
  }
})();
